import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Settings, CreditCard, LogOut } from 'lucide-react';
import AuthService from '../services/AuthService';
import ApiService from '../services/ApiService';
import CheckRemittanceApp from './CheckRemittanceApp';
import BillingSection from './billing/BillingSection';
import TeamSection from './team/TeamSection';
import TeamDashboard from './TeamDashboard';
import WorkOrderManagement from './WorkOrderManagement';
import CreateBillModal from './CreateBillModal';
import StagingArea from './StagingArea';
import SheetImport from './SheetImport';
import ClientApprovalImport from './ClientApprovalImport';
import ContactsScreen from './ContactsScreen';
import FinancialReports from './FinancialReports';
import DepartmentRevenueReport from './DepartmentRevenueReport';
import ContractorInviteManager from './ContractorInviteManager';
import ProductsManager from './ProductsManager';
import AllTasksView from './AllTasksView';
import XeroConnectionIndicator from './XeroConnectionIndicator';
import { useXeroConnection } from '../contexts/XeroConnectionContext';

const Dashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState(() => {
    // Load saved tab from localStorage, default to 'workorders'
    return localStorage.getItem('dashboardActiveTab') || 'workorders';
  });
  const [loading, setLoading] = useState(false);
  const [showCreateBillModal, setShowCreateBillModal] = useState(false);
  const [showXeroSettings, setShowXeroSettings] = useState(false);
  const [showClientApprovalImport, setShowClientApprovalImport] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [pendingOpenWorkOrderId, setPendingOpenWorkOrderId] = useState(null);
  const navigate = useNavigate();
  
  // Use the Xero connection context (auto-updates every 30 seconds)
  const { isConnected, tenantName, updateConnectionStatus } = useXeroConnection();

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.relative')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleConnectToXero = async () => {
    console.log('🔗 === STARTING XERO CONNECTION PROCESS ===');
    
    try {
      console.log('📡 Step 1: Checking if already connected...');
      // Check if already connected
      const status = await ApiService.getConnectionStatus();
      console.log('📊 Connection status response:', status);
      
      if (status.isConnected) {
        console.log('✅ Already connected to Xero!');
        updateConnectionStatus({ isConnected: true, tenantName: status.tenantName });
        alert('Already connected to Xero!');
        return;
      }

      console.log('🔑 Step 2: Getting authorization URL...');
      // Get authorization URL - use the same method as CheckRemittanceApp
      const response = await ApiService.getAuthorizationUrl();
      console.log('🌐 Authorization URL response:', response);
      const authUrl = response.authorizationUrl;
      console.log('🔗 Auth URL:', authUrl);
      
      console.log('🪟 Step 3: Opening authorization window...');
      // Open Xero authorization in new window
      const authWindow = window.open(authUrl, 'xero-auth', 'width=600,height=700');
      console.log('🪟 Auth window opened:', authWindow);
      
      // Listen for the callback
      const handleCallback = async (event) => {
        console.log('📨 Received callback message:', event);
        console.log('🌐 Event origin:', event.origin);
        console.log('🌐 Window origin:', window.location.origin);
        if (event.origin !== window.location.origin) {
          console.log('❌ Origin mismatch, ignoring message');
          return;
        }
        
        if (event.data.type === 'XERO_AUTH_SUCCESS') {
          console.log('✅ Auth success! Code received:', event.data.code);
          authWindow.close();
          
          try {
            console.log('🔄 Step 4: Exchanging code for token...');
            const tokenResponse = await ApiService.exchangeCodeForToken(event.data.code, event.data.state);
            console.log('🎫 Token response:', tokenResponse);
            
            if (tokenResponse.success && tokenResponse.connections?.length > 0) {
              const connection = tokenResponse.connections[0];
              console.log('🏢 Connection established:', connection);
              updateConnectionStatus({
                isConnected: true,
                tenantName: connection.tenantName
              });
              alert('Successfully connected to Xero!');
            } else {
              console.log('⚠️ Token exchange successful but no connections, checking status...');
              // The context will auto-refresh on next interval
            }
          } catch (error) {
            console.error('💥 Error during authorization:', error);
            alert('Failed to connect to Xero. Please try again.');
          }
        } else {
          console.log('📨 Received non-success message:', event.data);
        }
      };
      
      window.addEventListener('message', handleCallback);
      
      // Clean up listener when window is closed
      const checkClosed = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleCallback);
        }
      }, 1000);
      
    } catch (error) {
      console.error('💥 === XERO CONNECTION ERROR ===');
      console.error('Error details:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      alert(`Failed to connect to Xero: ${error.message}`);
    }
  };

  const handleDisconnectFromXero = async () => {
    if (!confirm('Are you sure you want to disconnect from Xero? You will need to reconnect to access Xero features.')) {
      return;
    }

    try {
      setLoading(true);
      const result = await ApiService.disconnectFromXero();
      
      if (result.success) {
        updateConnectionStatus({ isConnected: false, tenantName: null });
        alert('Successfully disconnected from Xero');
      } else {
        alert('Failed to disconnect from Xero');
      }
    } catch (error) {
      console.error('Error disconnecting from Xero:', error);
      alert('Error disconnecting from Xero: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Save tab preference to localStorage
    localStorage.setItem('dashboardActiveTab', tabId);
  };

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      onLogout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleBillCreated = (result) => {
    console.log('Bill created:', result);
    alert(`Bill created successfully: ${result.billNumber}`);
  };

  const handleApprovalImportComplete = (result) => {
    console.log('Client approvals imported:', result);
    alert(`Client approvals imported: ${result.successCount} successful, ${result.errorCount} errors`);
  };

  const tabs = [
    { id: 'workorders', name: 'Work Orders', icon: '📋' },
    { id: 'tasks', name: 'Tasks', icon: '✅' },
    { id: 'contacts', name: 'Contacts', icon: '👤' },
    { id: 'bills', name: 'Bills', icon: '📄' },
    { id: 'staging', name: 'Xero Staging', icon: '📤' },
    { id: 'sheetimport', name: 'Sheet Import', icon: '📊' },
    { id: 'payments', name: 'Payments', icon: '💳' },
    { id: 'team', name: 'Team', icon: '👥' },
    { id: 'reports', name: 'Reports', icon: '📈' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <div className="text-2xl text-blue-600">🏠</div>
                    <div className="absolute -top-0.5 -right-0.5 text-xs text-green-500">🛡️</div>
                  </div>
                  <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                    ACVentures
                  </div>
                </div>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-indigo-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                  >
                    <span>{tab.icon}</span>
                    <span>{tab.name}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Xero Connection Status - Auto-updates every 30s */}
              <XeroConnectionIndicator 
                compact={true} 
                showDetails={false}
                showActionButtons={true}
                onDisconnect={handleDisconnectFromXero}
              />

              {/* User Dropdown Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md transition-colors"
                >
                  <div className="text-right">
                    <div className="font-medium">{user.firstName} {user.lastName}</div>
                    <div className="text-xs text-gray-500">{user.tenantName}</div>
                  </div>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <button
                      onClick={() => {
                        handleTabChange('settings');
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                    >
                      <Settings className="w-4 h-4" />
                      Organization Settings
                    </button>
                    <button
                      onClick={() => {
                        handleTabChange('billing');
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                    >
                      <CreditCard className="w-4 h-4" />
                      Billing & Subscription
                    </button>
                    <hr className="my-2 border-gray-200" />
                    <button
                      onClick={() => {
                        handleLogout();
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile tab navigation */}
      <div className="sm:hidden bg-white border-b border-gray-200">
        <div className="flex space-x-4 px-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'text-indigo-600 border-indigo-600'
                  : 'text-gray-500 border-transparent'
              } flex-1 py-3 text-sm font-medium border-b-2 flex items-center justify-center space-x-1`}
            >
              <span>{tab.icon}</span>
              <span>{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Xero Connection Banner */}
      {!isConnected && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">
                  <strong>Not connected to Xero.</strong> Connect now to access contacts, create invoices, and sync data.
                </p>
              </div>
            </div>
            <div className="flex-shrink-0 flex gap-2">
              <button
                onClick={handleConnectToXero}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {loading ? 'Connecting...' : 'Connect to Xero'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {activeTab === 'payments' && (
          <div>
            <div className="px-4 py-6 sm:px-0">
              <div className="border-4 border-dashed border-gray-200 rounded-lg p-6">
                <CheckRemittanceApp />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'workorders' && (
          <div>
            <div className="px-4 py-6 sm:px-0">
              <WorkOrderManagement
                openWorkOrderId={pendingOpenWorkOrderId}
                onWorkOrderOpened={() => setPendingOpenWorkOrderId(null)}
              />
            </div>
          </div>
        )}

        {activeTab === 'staging' && (
          <div>
            <div className="px-4 py-6 sm:px-0">
              <StagingArea />
            </div>
          </div>
        )}

        {activeTab === 'sheetimport' && (
          <div>
            <div className="px-4 py-6 sm:px-0">
              <SheetImport />
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div>
            <div className="px-4 py-6 sm:px-0">
              <ContactsScreen />
            </div>
          </div>
        )}


        {activeTab === 'bills' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Bills & Expenses
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Create bills for suppliers and contractors. Bills represent money you owe to others.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowCreateBillModal(true)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <span className="text-lg">📄</span>
                      Create Bill
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-red-50 rounded-lg p-4">
                      <h4 className="font-semibold text-red-800 mb-2">What is a Bill?</h4>
                      <p className="text-sm text-red-700">
                        A bill is an invoice from a supplier or contractor that you need to pay. 
                        It represents money you owe for goods or services received.
                      </p>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-800 mb-2">When to Use</h4>
                      <p className="text-sm text-blue-700">
                        • Subcontractor invoices<br/>
                        • Supplier bills<br/>
                        • Service provider invoices<br/>
                        • Material purchases
                      </p>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 mb-2">Benefits</h4>
                      <p className="text-sm text-green-700">
                        • Track what you owe<br/>
                        • Manage due dates<br/>
                        • Expense tracking<br/>
                        • Xero integration
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Quick Start</h4>
                    <ol className="text-sm text-gray-700 space-y-1">
                      <li>1. Click "Create Bill" above</li>
                      <li>2. Select the supplier/contractor</li>
                      <li>3. Enter the bill details (amount, description, dates)</li>
                      <li>4. The bill will be created in Xero for tracking and payment</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="px-4 py-6 sm:px-0 space-y-6">
            {/* Xero Connection Status */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Xero Integration
                </h3>
                <XeroConnectionIndicator 
                  showDetails={true} 
                  showActionButtons={true}
                  onDisconnect={handleDisconnectFromXero}
                  onReconnect={handleConnectToXero}
                />
                
                {!isConnected && (
                  <div className="mt-4">
                    <button
                      onClick={handleConnectToXero}
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Connecting...' : 'Connect to Xero'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Organization Settings */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Organization Settings
                </h3>
                <div className="mt-2 max-w-xl text-sm text-gray-500">
                  <p>Manage your organization's settings and preferences.</p>
                </div>
                <div className="mt-5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Organization Name
                    </label>
                    <input
                      type="text"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      defaultValue={user.tenantName}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      defaultValue={user.email}
                    />
                  </div>
                  <div className="pt-4">
                    <button
                      type="button"
                      className="bg-indigo-600 text-white px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Team Members */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <TeamSection user={user} />
              </div>
            </div>

            {/* Products & Services */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <ProductsManager />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="px-4 py-6 sm:px-0">
            <BillingSection user={user} />
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="px-4 py-6 sm:px-0">
            <DepartmentRevenueReport />
            <FinancialReports />
          </div>
        )}

        {activeTab === 'contractors' && (
          <div className="px-4 py-6 sm:px-0">
            <ContractorInviteManager />
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Follow-up Tasks</h2>
                <p className="text-sm text-gray-500 mt-0.5">All tasks across every work order.</p>
              </div>
              <AllTasksView onViewWorkOrder={(workOrderId) => {
                setPendingOpenWorkOrderId(workOrderId);
                handleTabChange('workorders');
              }} />
            </div>
          </div>
        )}

        {activeTab === 'team' && (
          <div className="px-4 py-6 sm:px-0">
            <TeamDashboard />
          </div>
        )}
      </main>

      {/* Create Bill Modal */}
      {showCreateBillModal && (
        <CreateBillModal
          onClose={() => setShowCreateBillModal(false)}
          onBillCreated={handleBillCreated}
        />
      )}
    </div>
  );
};

export default Dashboard;
